// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { StepperContext, withStepperContext } from "@guestsupply/App/Widgets/Stepper/StepperContext";
import { customXngageWidget } from "@guestsupply/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext } from "react";

interface OwnProps {}

type Props = OwnProps;

export interface StepperStyles {
    container?: InjectableCss;
    stepWrapper?: InjectableCss;
    stepNumber?: TypographyProps;
    stepLabel?: TypographyProps;
    stepSeparator?: InjectableCss;
}

export const styles: StepperStyles = {};

export const Stepper: React.FC<Props> = () => {
    const { steps, currentStep, allowStepClick, moveToStepHandler } = useContext(StepperContext);

    const StepperComponent = () => {
        return (
            <StyledWrapper {...styles.container} className="stepper" aria-label="Stepper">
                {steps.map((item, index) => {
                    const isCompleted = item.index < currentStep;
                    const isActive = item.index === currentStep;
                    const isLastStep = steps.length - 1 === index;
                    const stepStatus = isActive ? "active" : isCompleted ? "complete" : "incomplete";
                    return (
                        <>
                            <StyledWrapper
                                {...styles.stepWrapper}
                                data-clickable={`${isActive || isCompleted}`}
                                className="step-wrapper"
                                data-status={stepStatus}
                                key={item.index}
                                onClick={() => isCompleted && allowStepClick && moveToStepHandler(item.index)}
                            >
                                <Typography {...styles.stepNumber} data-status={stepStatus} className="step-number">
                                    {item.index}
                                </Typography>
                                <Typography {...styles.stepLabel} data-status={stepStatus} className="step-label">
                                    {item.displayName || item.name}
                                </Typography>
                            </StyledWrapper>
                            {!isLastStep && (
                                <StyledWrapper
                                    role="separator"
                                    data-status={stepStatus}
                                    className="step-separator"
                                    {...styles.stepSeparator}
                                />
                            )}
                        </>
                    );
                })}
            </StyledWrapper>
        );
    };

    return <StepperComponent />;
};

const widgetModule: WidgetModule = {
    component: withStepperContext(Stepper),
    definition: {
        group: customXngageWidget as any,
        displayName: "Stepper",
        fieldDefinitions: [],
    },
};

export default widgetModule;
